import { parse } from 'cookie';
import type { GraphQLFormattedError } from 'graphql/error/GraphQLError';

export const isIPhone = (userAgent: string): boolean =>
  Boolean(userAgent) && /iPhone/.test(userAgent);

export const isAndroid = (userAgent: string): boolean =>
  Boolean(userAgent) && /Android/.test(userAgent);

export const getDeviceType = (userAgent: string): string => {
  if (isIPhone(userAgent)) {
    return 'iPhone';
  }
  if (isAndroid(userAgent)) {
    return 'Android';
  }
  return 'Other';
};

export const removeUndefined = (obj: any) => JSON.parse(JSON.stringify(obj));

export const getCurrentDate = (): Date => new Date();

export const isElementInViewport = function (element?: HTMLElement | null) {
  if (!element) {
    return false;
  }

  const boundingClient = element.getBoundingClientRect();
  const height = window.innerHeight || document.documentElement.clientHeight;
  const width = window.innerWidth || document.documentElement.clientWidth;

  return (
    boundingClient.top >= 0 &&
    boundingClient.left >= 0 &&
    boundingClient.bottom <= height &&
    boundingClient.right <= width
  );
};

export const triggerHotjarSurvey = (survey: string) => {
  if (typeof window !== 'undefined' && typeof window.hj === 'function') {
    // @ts-expect-error
    window.hj('trigger', survey);
  }
};

export const navigateToUrl = (destination?: string, replace = false) => {
  if (typeof window === 'undefined' || !destination) {
    return;
  }

  if (replace) {
    window.location.replace(destination);
  } else {
    window.location.assign(destination);
  }
};

export const openNewTab = (destination?: string) => {
  if (window && destination) {
    setTimeout(() => {
      window.open(destination, '_blank');
    }, 500);
  }
};

export const getSessionId = () =>
  parse(window.document.cookie).JobseekerSessionId || '';

export type Unpromise<T extends Promise<any>> =
  T extends Promise<infer U> ? U : never;

export function notEmpty<TValue>(
  value: TValue | null | undefined,
): value is TValue {
  return value !== null && value !== undefined;
}

export const getRRQuestionId = (rr: any, regexObj: RegExp) =>
  Object.keys(rr).find((key) => key.match(regexObj));

export const getUrlCorrelationId = (): string | null => {
  const search = typeof window !== 'undefined' ? location.search : '';
  const searchParams = new URLSearchParams(search);
  const urlCorrelationId = searchParams.get('correlationid') || '';

  if (typeof urlCorrelationId === 'string') {
    return urlCorrelationId;
  }

  return urlCorrelationId[0];
};

export function didResolverErrorOccur(
  pathICareAbout: string[],
  errors: readonly GraphQLFormattedError[],
) {
  return errors.some(
    (error: GraphQLFormattedError) =>
      error.path?.length &&
      (isPrefix(pathICareAbout, error.path) ||
        isPrefix(error.path, pathICareAbout)),
  );
}

function isPrefix(
  prefix: ReadonlyArray<string | number>,
  array: ReadonlyArray<string | number>,
) {
  return prefix.every((value, index) => value === array[index]);
}

export function isFile(file: unknown): file is File {
  return file instanceof File;
}

// const result = await asyncPoll(fetchSth, (result) => result === 'done', 5)
export async function asyncPoll<T>(
  fn: () => Promise<T>,
  predicate: (result: T) => boolean,
  maxAttempts = 2,
  delay = 1000,
) {
  const result = await fn();

  if (predicate(result) || maxAttempts === 0) {
    return result;
  }

  await new Promise((resolve) => setTimeout(resolve, delay));

  return asyncPoll(fn, predicate, maxAttempts - 1, delay);
}

export function normalizeName(name: string) {
  return name.trim().split(/\s+/).filter(Boolean).join(' ').toUpperCase();
}
